import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Upload, Mic, MicVocal, Square, Trash2, CassetteTape } from 'lucide-react';
import './App.css';

import { NoteEvent, Frame, AnalysisData } from './types';
import { slopeColor, noteIntensity, fmt } from './utils';
import { WavRecorder } from './WavRecorder';
import { AudioEngine } from './AudioEngine';
import { analyzeAudio } from './analysis';
import { Waveform } from './components/Waveform';
import { PianoRoll } from './components/PianoRoll';
import { HarmonicBars } from './components/HarmonicBars';
import { VowelChart } from './components/VowelChart';

// ── Components ─────────────────────────────────────────────

function NoteDetail({ note, frames }: { note: NoteEvent; frames?: Frame[] }) {
  const noteFrames = useMemo(() => {
    if (!frames || !note.frame_indices) return [];
    return note.frame_indices.map(i => frames[i]).filter(Boolean);
  }, [note, frames]);

  const Metric = ({ label, value, unit }: { label: string; value: string; unit?: string }) => (
    <div className="nd-metric">
      <span className="nd-label">{label}</span>
      <span className="nd-value">{value}{unit && <span className="nd-unit"> {unit}</span>}</span>
    </div>
  );

  // F0 source breakdown
  const f0Sources: Record<string, number> = {};
  const f0Confidences: number[] = [];
  noteFrames.forEach(f => {
    f0Sources[f.f0_source] = (f0Sources[f.f0_source] || 0) + 1;
    if (f.f0_confidence !== null) f0Confidences.push(f.f0_confidence);
  });
  const avgF0Conf = f0Confidences.length > 0
    ? f0Confidences.reduce((a, b) => a + b, 0) / f0Confidences.length
    : null;

  // Collect evidence from frames
  // Collect evidence from frames
  const allEvidence = new Set<string>();
  noteFrames.forEach(f => {
    if (f.coordination.evidence) {
      f.coordination.evidence.forEach((e: string) => allEvidence.add(e));
    }
  });

  // Average harmonics from frames
  const avgHarmonics = [0, 1, 2, 3, 4, 5].map(hi => {
    const vals = noteFrames
      .map(f => f.harmonics_db[hi])
      .filter(v => v !== null) as number[];
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  });

  return (
    <div className="note-detail" style={{ borderTopColor: slopeColor(noteIntensity(note, frames)) }}>
      <div className="nd-header">
        <span className="nd-note-name">{note.note}</span>
        <span className="nd-shape" style={{ color: slopeColor(noteIntensity(note, frames)) }}>
          {noteIntensity(note, frames) != null ? noteIntensity(note, frames)!.toFixed(1) + ' dB' : '–'}
        </span>
        <span className="nd-time">{note.start_s.toFixed(2)}–{note.end_s.toFixed(2)}s</span>
        <span className="nd-dur">{((note.end_s - note.start_s) * 1000).toFixed(0)}ms</span>
        <span className="nd-conf">conf {note.shape_confidence.toFixed(2)}</span>
      </div>

      <div className="nd-grid">
        <Metric label="F0" value={fmt(note.f0_median_hz, 1)} unit="Hz" />
        <Metric label="F0 Source" value={Object.entries(f0Sources).map(([k,v]) => `${k}:${v}`).join(' ')} />
        <Metric label="F0 Conf" value={avgF0Conf !== null ? fmt(avgF0Conf, 3) : '–'} />
        <Metric label="H1*-H2*" value={fmt(note.corrected_h1h2_median_db)} unit="dB" />
        <Metric label="OQ" value={fmt(note.oq_median)} />
        <Metric label="NAQ" value={fmt(note.naq_median, 3)} />
        <Metric label="CPP" value={fmt(note.cpp_median_db)} unit="dB" />
        <Metric label="HNR" value={fmt(note.hnr_median_db)} unit="dB" />
        <Metric label="Jitter" value={fmt(note.jitter_median_pct)} unit="%" />
        <Metric label="Shimmer" value={fmt(note.shimmer_median_pct)} unit="%" />
        <Metric label="Slope" value={fmt(note.spectral_slope_median)} unit="dB/H" />
        <Metric label="Aperiodicity" value={fmt(note.aperiodicity_median)} />
        <Metric label="Intensity" value={fmt(note.intensity_median_db, 1)} unit="dB" />
        <Metric label="Frames" value={String(note.frame_count)} />
      </div>

      {noteFrames.length > 0 && (
        <div className="nd-harmonics">
          <span className="nd-label">Harmonics</span>
          <div className="h-bars">
            {avgHarmonics.map((h, hi) => {
              // Treat values below -100 dB as no meaningful harmonic (noise floor)
              const valid = h !== null && h > -100;
              const pct = valid ? Math.max(4, ((h! + 70) / 70) * 100) : 0;
              return (
                <div key={hi} className="h-bar-col">
                  <div className="h-bar-area">
                    <div className="h-bar" style={{
                      height: `${pct}%`,
                      backgroundColor: slopeColor(noteIntensity(note, frames)),
                      opacity: valid ? 0.8 : 0.15,
                    }} />
                  </div>
                  <span className="h-label">H{hi + 1}</span>
                  <span className="h-val">{valid ? h!.toFixed(0) : '–'}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {allEvidence.size > 0 && (
        <div className="nd-evidence converging">
          <span className="ev-title">Evidence</span>
          {Array.from(allEvidence).map((e, i) => <span key={i} className="ev-line">{e}</span>)}
        </div>
      )}

      {note.flags.length > 0 && (
        <div className="nd-flags">
          {note.flags.map(f => <span key={f} className="flag-tag">{f}</span>)}
        </div>
      )}
    </div>
  );
}

// ── Analysis Lane ─────────────────────────────────────────

// Global active lane tracking for spacebar
let globalActiveLaneId: number | null = null;
let laneIdCounter = 0;

function AnalysisLane({ defaultUrl, onRemove, showFullEmpty, onLoadBlob, initialBlob }: { defaultUrl?: string; onRemove?: () => void; showFullEmpty?: boolean; onLoadBlob?: (blob: Blob) => void; initialBlob?: Blob }) {
  const [laneId] = useState(() => laneIdCounter++);
  const [data, setData] = useState<AnalysisData | null>(null);
  const [selectedNote, setSelectedNote] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const engineRef = useRef<AudioEngine | null>(null);

  useEffect(() => {
    if (defaultUrl) {
      fetch('/analysis.json')
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) { setData(d); setAudioUrl(defaultUrl); setSelectedNote(d.notes?.length > 0 ? 0 : null); } })
        .catch(() => {});
    } else if (initialBlob) {
      // Auto-analyze a blob (from live recording) — client-side via Pyodide
      (async () => {
        setIsAnalyzing(true);
        try {
          const result = await analyzeAudio(initialBlob, setAnalyzeStatus);
          setData(result);
          setSelectedNote(result.notes?.length > 0 ? 0 : null);
          setAudioUrl(URL.createObjectURL(initialBlob));
        } catch { /* ignore */ }
        finally { setIsAnalyzing(false); setAnalyzeStatus(''); }
      })();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set this lane as active on any interaction
  const activateLane = useCallback(() => { globalActiveLaneId = laneId; }, [laneId]);

  // First lane is active by default
  useEffect(() => {
    if (globalActiveLaneId === null) globalActiveLaneId = laneId;
  }, [laneId]);

  // Register this lane's engine globally for spacebar control
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && e.target === document.body && globalActiveLaneId === laneId) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const engine = engineRef.current;
        if (engine) {
          engine.playPause();
          setIsPlaying(engine.isPlaying);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true); // capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [laneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [loopRegion, setLoopRegion] = useState<{ start: number; end: number } | null>(null);

  // Loop enforcement — only loop if playback naturally crossed the end
  const prevTimeRef = useRef(0);
  useEffect(() => {
    if (!loopRegion || !isPlaying || !engineRef.current) return;
    const prev = prevTimeRef.current;
    const t = currentTime;
    prevTimeRef.current = t;

    // Natural playback: prev was inside loop, now past the end
    // Skip if it looks like a manual seek (big jump)
    const dt = Math.abs(t - prev);
    if (dt > 0.5) return; // manual seek, don't loop

    if (prev >= loopRegion.start && prev < loopRegion.end && t >= loopRegion.end) {
      engineRef.current.seek(loopRegion.start);
    }
  }, [currentTime, loopRegion, isPlaying]);

  // Seek to note start when selected
  const handleSelectNote = useCallback((i: number | null, clickTime?: number) => {
    setSelectedNote(i);
    if (engineRef.current && clickTime != null && clickTime >= 0) {
      engineRef.current.seek(clickTime);
      setCurrentTime(clickTime);
    }
  }, []);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeStatus, setAnalyzeStatus] = useState('');
  const [formantPos, setFormantPos] = useState<{ f1: number; f2: number } | null>(null);
  const [showRulers, setShowRulers] = useState(true);
  const [synthProfile, setSynthProfile] = useState<{ freq: number; db: number; bw?: number }[] | null>(null);
  const [praatFormants, setPraatFormants] = useState<Record<string, { freq: number | null; bw: number | null }> | null>(null);
  // Filter regions for isolated listening
  interface FilterRegion { id: number; lo: number; hi: number; gain: number }
  const [filterRegions, setFilterRegions] = useState<FilterRegion[]>([]);
  const [filterActive, setFilterActive] = useState(false);
  const filterNodesRef = useRef<BiquadFilterNode[]>([]);
  const filterGainRef = useRef<GainNode | null>(null);
  const filterRegionGainsRef = useRef<GainNode[]>([]);
  const nextFilterId = useRef(1);

  const addFilterRegion = useCallback(() => {
    if (filterRegions.length >= 5) return;
    setFilterRegions(prev => [...prev, {
      id: nextFilterId.current++,
      lo: 800,
      hi: 1200,
      gain: 1.0,
    }]);
    setFilterActive(true);
  }, [filterRegions.length]);

  const removeFilterRegion = useCallback((id: number) => {
    setFilterRegions(prev => {
      const next = prev.filter(r => r.id !== id);
      if (next.length === 0) setFilterActive(false);
      return next;
    });
  }, []);

  const updateFilterRegion = useCallback((id: number, updates: Partial<FilterRegion>) => {
    setFilterRegions(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  // Apply filters via AudioEngine's audio graph
  // Engine: source → gainNode. We reroute: gainNode → [filters] → destination
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (!filterActive && filterRegions.length === 0 && filterNodesRef.current.length === 0) return;

    const ctx = engine.ctx;
    const engineGain = engine.getOutputNode();

    engineGain.disconnect();
    filterNodesRef.current.forEach(n => n.disconnect());
    if (filterGainRef.current) filterGainRef.current.disconnect();
    filterNodesRef.current = [];

    if (!filterActive || filterRegions.length === 0) {
      engineGain.connect(ctx.destination);
    } else {
      if (!filterGainRef.current) filterGainRef.current = ctx.createGain();
      const fGain = filterGainRef.current;
      fGain.gain.value = 1.0;
      const allNodes: BiquadFilterNode[] = [];
      const regionGains: GainNode[] = [];

      for (const region of filterRegions) {
        let chain: AudioNode = engineGain;
        for (let i = 0; i < 3; i++) {
          const hp = ctx.createBiquadFilter();
          hp.type = 'highpass';
          hp.frequency.value = region.lo;
          hp.Q.value = 0.7071;
          chain.connect(hp);
          chain = hp;
          allNodes.push(hp);
        }
        for (let i = 0; i < 3; i++) {
          const lp = ctx.createBiquadFilter();
          lp.type = 'lowpass';
          lp.frequency.value = region.hi;
          lp.Q.value = 0.7071;
          chain.connect(lp);
          chain = lp;
          allNodes.push(lp);
        }
        // Per-region gain node
        const rGain = ctx.createGain();
        rGain.gain.value = region.gain;
        chain.connect(rGain);
        rGain.connect(fGain);
        regionGains.push(rGain);
      }
      fGain.connect(ctx.destination);
      filterNodesRef.current = allNodes;
      filterRegionGainsRef.current = regionGains;
    }
  }, [filterActive, filterRegions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth filter frequency updates during drag
  useEffect(() => {
    if (!filterActive || !engineRef.current || filterNodesRef.current.length === 0) return;
    const ctx = engineRef.current.ctx;
    const rampTime = ctx.currentTime + 0.02;
    let nodeIdx = 0;
    for (let ri = 0; ri < filterRegions.length; ri++) {
      const region = filterRegions[ri];
      for (let i = 0; i < 3; i++) {
        const node = filterNodesRef.current[nodeIdx];
        if (node) {
          node.frequency.cancelScheduledValues(ctx.currentTime);
          node.frequency.linearRampToValueAtTime(region.lo, rampTime);
        }
        nodeIdx++;
      }
      for (let i = 0; i < 3; i++) {
        const node = filterNodesRef.current[nodeIdx];
        if (node) {
          node.frequency.cancelScheduledValues(ctx.currentTime);
          node.frequency.linearRampToValueAtTime(region.hi, rampTime);
        }
        nodeIdx++;
      }
      // Per-region gain
      const rGain = filterRegionGainsRef.current[ri];
      if (rGain) {
        rGain.gain.cancelScheduledValues(ctx.currentTime);
        rGain.gain.linearRampToValueAtTime(region.gain, rampTime);
      }
    }
  }, [filterRegions, filterActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const [audioUrl, setAudioUrl] = useState('/audio.mp3');
  const [isRecording, setIsRecording] = useState(false);
  const [recCountdown, setRecCountdown] = useState(0); // 0 = not counting down
  const wavRecorderRef = useRef<WavRecorder | null>(null);
  const liveChunksRef = useRef<Float32Array[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const rec = new WavRecorder();
      setRecCountdown(Date.now()); // store start time for progress

      // Phase 1: init mic (this takes 500-1500ms — hidden by countdown)
      await rec.init();

      // Live waveform callback
      liveChunksRef.current = [];
      rec.onChunk = (chunk) => { liveChunksRef.current.push(chunk); };

      // Wait for countdown to finish (1.5s total, mic init already took some)
      await new Promise(r => setTimeout(r, 500));

      // Phase 2: start capturing
      rec.startCapture();
      wavRecorderRef.current = rec;
      setRecCountdown(0);
      setIsRecording(true);
    } catch {
      setRecCountdown(0);
      alert('Microphone access denied.');
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (!wavRecorderRef.current) return;
    const blob = await wavRecorderRef.current.stop();
    wavRecorderRef.current = null;
    setIsRecording(false);

    setIsAnalyzing(true);
    try {
      const result = await analyzeAudio(blob, setAnalyzeStatus);
      setData(result);
      setSelectedNote(result.notes?.length > 0 ? 0 : null);
      setAudioUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      alert(`Analysis failed: ${err.message || err}`);
    } finally {
      setIsAnalyzing(false);
      setAnalyzeStatus('');
    }
  }, []);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // If it's a JSON file, load directly (backwards compat)
    if (file.name.endsWith('.json')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          setData(parsed);
          setSelectedNote(parsed.notes?.length > 0 ? 0 : null);
        } catch { /* ignore */ }
      };
      reader.readAsText(file);
      return;
    }

    // Audio file — analyze client-side via Pyodide (falls back to server)
    setIsAnalyzing(true);
    try {
      const result = await analyzeAudio(file, setAnalyzeStatus);
      setData(result);
      setSelectedNote(result.notes?.length > 0 ? 0 : null);
      setAudioUrl(URL.createObjectURL(file));
    } catch (err: any) {
      alert(`Analysis failed: ${err.message || err}`);
    } finally {
      setIsAnalyzing(false);
      setAnalyzeStatus('');
    }
  }, []);

  // Create placeholder data for empty state
  const emptyData: AnalysisData = data || {
    metadata: { filename: 'No audio loaded', duration_s: 0, sample_rate: 0, analyzer_version: '' },
    calibration: { estimated_voice_type: '', pitch_floor_hz: 0, pitch_ceiling_hz: 0, calibration_notes: [] },
    interpretation_guide: {},
    notes: [],
    analysis_summary: { total_frames: 0, flagged_frames: 0, flag_counts: {} },
  };
  const hasData = !!data;

  return (
    <div className="lane" onClick={activateLane}>
      {onRemove && (
        <button className="lane-delete" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove analysis">
          <Trash2 size={14} />
        </button>
      )}

      {(hasData || isRecording || recCountdown > 0) ? <Waveform
        audioUrl={audioUrl}
        duration={data?.metadata?.duration_s || 0}
        onTimeUpdate={setCurrentTime}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        engineRef={engineRef}
        currentTime={currentTime}
        onUpload={handleFileUpload}
        onRecord={startRecording}
        onStopRecord={stopRecording}
        isRecording={isRecording}
        recCountdown={recCountdown}
        isAnalyzing={isAnalyzing}
        analyzeStatus={analyzeStatus}
        filename={emptyData.metadata.filename}
        liveChunksRef={liveChunksRef}
      /> : (
        <div className="waveform-container">
          <div className="waveform-controls">
            <button className="play-btn" disabled>▶</button>
            <label className="control-btn" title="Upload audio">
              <Upload size={14} />
              <input type="file" accept=".mp3,.wav,.flac,.ogg,.m4a,.aac,.json" onChange={handleFileUpload} hidden disabled={isAnalyzing || isRecording} />
            </label>
            <button
              className={`control-btn ${isRecording ? 'recording' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isAnalyzing}>
              {isRecording ? <Square size={14} /> : <Mic size={14} />}
            </button>
            <span className="waveform-filename">
              {isAnalyzing ? (analyzeStatus || 'Analyzing...') : isRecording ? 'Recording...' : 'No audio loaded'}
            </span>
            <span className="waveform-spacer" />
            <span className="waveform-time">0:00.0</span>
              </div>
          <div className="waveform-empty">Upload or record audio</div>
        </div>
      )}

      {(hasData || showFullEmpty) && <>
      <PianoRoll
        notes={emptyData.notes}
        duration={emptyData.metadata.duration_s || 1}
        currentTime={currentTime}
        selectedNote={selectedNote}
        onSelectNote={handleSelectNote}
        pitchContour={emptyData.pitch_contour}
        frames={emptyData.frames}
        loopRegion={loopRegion}
        onLoopChange={setLoopRegion}
      />

      <div className="analysis-panels">
        <VowelChart
          formantPos={formantPos}
          onFormantChange={setFormantPos}
          showRulers={showRulers}
          onToggleRulers={() => setShowRulers(r => !r)}
          selectedNote={selectedNote !== null ? emptyData.notes[selectedNote] : null}
          allNotes={emptyData.notes}
          spectrogram={emptyData.spectrogram}
          pitchContour={emptyData.pitch_contour}
          audioUrl={audioUrl}
          onSynthProfileChange={setSynthProfile}
          currentTime={currentTime}
          onMuteAudio={(mute) => {
            if (engineRef.current) engineRef.current.gainNode.gain.value = mute ? 0 : 1;
          }}
          isAudioPlaying={isPlaying}
          onPraatFormants={setPraatFormants}
        />
        {emptyData.spectrogram ? (
          <HarmonicBars
            spectrogram={emptyData.spectrogram}
            currentTime={currentTime}
            formantPos={formantPos}
            onFormantChange={setFormantPos}
            showRulers={showRulers}
            filterRegions={filterRegions}
            filterActive={filterActive}
            onAddFilter={addFilterRegion}
            onRemoveFilter={removeFilterRegion}
            onUpdateFilter={updateFilterRegion}
            onToggleFilter={() => setFilterActive(a => !a)}
            onLoadLiveRecording={onLoadBlob}
            synthProfile={synthProfile}
            praatFormants={praatFormants}
            onFormantChangeLive={setFormantPos}
            onPraatFormants={setPraatFormants}
          />
        ) : (
          <div className="harmonic-bars-container">
            <div className="harmonic-bars-canvas" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 12 }}>
              No audio loaded
            </div>
          </div>
        )}
      </div>
      </>}

      {/* Note detail hidden for now
      {selectedNote !== null && emptyData.notes[selectedNote] && (
        <NoteDetail note={emptyData.notes[selectedNote]} frames={emptyData.frames} />
      )}

      {selectedNote === null && (
        <div className="hint">Click a note in the piano roll to see its analysis</div>
      )}
      */}
    </div>
  );
}

// ── App ────────────────────────────────────────────────────

function App() {
  const [lanes, setLanes] = useState<{ id: number; blob?: Blob }[]>([{ id: 0 }]);
  const nextId = useRef(1);

  const addLane = useCallback(() => {
    if (lanes.length >= 5) return;
    setLanes(prev => [...prev, { id: nextId.current++ }]);
  }, [lanes.length]);

  const addLaneWithBlob = useCallback((blob: Blob) => {
    if (lanes.length >= 5) return;
    setLanes(prev => [...prev, { id: nextId.current++, blob }]);
  }, [lanes.length]);

  const removeLane = useCallback((id: number) => {
    setLanes(prev => prev.filter(l => l.id !== id));
  }, []);

  return (
    <div className="app">
      <div className="top-bar">
        <h1 className="logo">jennie <MicVocal size={18} /></h1>
      </div>

      {lanes.map((lane, i) => (
        <AnalysisLane
          key={lane.id}
          defaultUrl={i === 0 ? '/audio.mp3' : undefined}
          onRemove={lanes.length > 1 ? () => removeLane(lane.id) : undefined}
          showFullEmpty={i === 0}
          onLoadBlob={addLaneWithBlob}
          initialBlob={lane.blob}
        />
      ))}

      {lanes.length < 5 && (
        <button className="add-lane-btn" onClick={addLane} title="Add analysis">
          <CassetteTape size={16} />
        </button>
      )}
    </div>
  );
}

export default App;
